// Sale pricing utility functions

export interface SalePricing {
  sale_price: number | null;
  sale_starts_at: string | null;
  sale_ends_at: string | null;
  price: number;
}

/**
 * Check if an asset is currently on sale
 */
export function isOnSale(asset: SalePricing): boolean {
  if (!asset.sale_price || asset.sale_price <= 0) return false;
  if (asset.sale_price >= asset.price) return false;
  
  const now = new Date();
  
  if (asset.sale_starts_at && new Date(asset.sale_starts_at) > now) return false;
  if (asset.sale_ends_at && new Date(asset.sale_ends_at) < now) return false;
  
  return true;
}

/**
 * Get the effective price (sale price if on sale, otherwise regular price)
 */
export function getEffectivePrice(asset: SalePricing): number {
  return isOnSale(asset) ? asset.sale_price! : asset.price;
}

/**
 * Get the discount percentage for an asset on sale
 */
export function getDiscountPercentage(asset: SalePricing): number {
  if (!isOnSale(asset)) return 0;
  return Math.round((1 - asset.sale_price! / asset.price) * 100);
}

/**
 * Format a price as USD currency
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(price);
}

/**
 * Get time remaining until sale ends
 */
export function getSaleTimeRemaining(endDate: string | null): string | null {
  if (!endDate) return null;
  
  const end = new Date(endDate);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  
  if (diff <= 0) return null;
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (days > 0) return `${days}d ${hours}h left`;
  
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  
  return `${minutes}m left`;
}
